import React from "react";
import { motion } from "framer-motion";
import { Camera, BookOpen, Truck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const steps = [
  {
    icon: Camera,
    step: "1",
    title: "Upload a Photo",
    description: "Just one clear photo of your child's face. That's all we need.",
    color: "bg-rose-100 text-rose-500",
    ring: "ring-rose-200",
  },
  {
    icon: BookOpen,
    step: "2",
    title: "Pick a Story",
    description: "Choose from magical adventures — jungle safari, space mission, desi festival fun, and more.",
    color: "bg-amber-100 text-amber-600",
    ring: "ring-amber-200",
  },
  {
    icon: Truck,
    step: "3",
    title: "We Print & Deliver",
    description: "We create a beautiful hardcover book and deliver it to your door in 5-7 days.",
    color: "bg-indigo-100 text-indigo-500",
    ring: "ring-indigo-200",
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15 } },
};

const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20 sm:py-28 bg-white relative overflow-hidden" data-testid="how-it-works-section">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,191,36,0.08),transparent_50%)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-bold text-rose-500 uppercase tracking-widest mb-3 block">Simple Process</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-indigo-950 tracking-tight mb-4" data-testid="how-it-works-heading">
            It's Easier Than Ordering on Swiggy
          </h2>
          <p className="text-base sm:text-lg text-indigo-950/50 max-w-lg mx-auto">
            Three simple steps to create a one-of-a-kind storybook your child will love.
          </p>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-3 gap-8 lg:gap-12"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
        >
          {steps.map((s, i) => (
            <motion.div key={i} variants={item} className="relative group">
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-[calc(50%+3rem)] right-[calc(-50%+3rem)] h-[2px] bg-gradient-to-r from-rose-200 to-amber-200 z-0" />
              )}

              <div className="relative z-10 bg-white rounded-3xl p-8 border border-indigo-950/5 shadow-xl shadow-rose-900/5 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300" data-testid={`step-card-${i + 1}`}>
                {/* Step number */}
                <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-rose-500/30">
                  {s.step}
                </div>

                <div className={`w-16 h-16 rounded-2xl ${s.color} flex items-center justify-center mb-6 ring-4 ${s.ring} ring-offset-2 ring-offset-white group-hover:scale-110 transition-transform duration-300`}>
                  <s.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-indigo-950 mb-3">{s.title}</h3>
                <p className="text-sm text-indigo-950/50 leading-relaxed">{s.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-14"
        >
          <Button
            data-testid="start-creating-button"
            className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-bold text-base px-10 py-6 shadow-xl shadow-rose-500/25 hover:-translate-y-1 hover:shadow-2xl transition-all group"
            onClick={() => document.getElementById("story-preview")?.scrollIntoView({ behavior: "smooth" })}
          >
            Start Creating Now
            <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
